import { Grid, Hidden } from '@material-ui/core';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Wysiwyg } from '../../../../../ECDApp/global/components/wysiwyg';
import { FormField } from '../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { radiogroupStyles } from '../../../../Global/constants/theme/styles/components/radiogroup-styles';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { useQuery } from '../../../../global/hooks/useQuery';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ArcRoutes } from '../../../arc/constants/routes';
import { AccountInfoRoutes } from '../../constants/routes';
import { FormTypes } from '../../models/form-models';
import { DependentsState, DependentTypes } from '../dependents/dependents-model';
import { PointOfContactDepentType, PointOfContactFormModel } from '../mycpg-midyear/models/mycpg-mid-year-models';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { stageDocuments } from '../personal-info/document-upload-slice';
import PointOfContact from '../point-of-contact/point-of-contact';
import { DependentDisplay } from '../relationships/components/dependent-display';
import { DeathOfChildAgreements } from './components/death-of-child-agreements';
import { DeathOfChildFormModel } from './model/death-of-child-form-models';
import { DeathOfChildWizard } from './wizard/death-of-child-wizard';

interface Props {
    wizard: DeathOfChildWizard;
}

interface DomesticPartnerState {
    values: DeathOfChildFormModel;
    formikHelpers: FormikProps<DeathOfChildFormModel>;
    documentUploadState: DocumentUploadState;
}

export function DeathOfChildForm(props: Props) {
    const { wizard } = props;

    const dispatch = useDispatch();
    const app = useContext(DemographicsContext);

    const apiErrors = useApiResponseError();
    const initialValues = wizard?.getInitialFormValue() ?? {};
    const gpsItem = useGpsType(wizard?.getGpsType());
    const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);

    const dependentsState = useSelector<TcsmStates, DependentsState>(state => state?.dependents[app]);
    const documentUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);

    const sucessFormType: FormTypes[] = useQuery()?.get('success')?.split(',')?.map(a => a as FormTypes);

    const [showPointOfContact, setShowPointOfContact] = useState(false);
    const [currentDomesticPartnerState, setCurrentDomesticPartnerState] = useState<DomesticPartnerState>(null);

    const dependentType: PointOfContactDepentType = PointOfContactDepentType.Child;
    const formType: FormTypes = app === DemographicAppKeys.myCPG ? FormTypes.DEATH_OF_CHILD : FormTypes.MAP_DEATH_OF_CHILD;

    const relationShipPathMap = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${AccountInfoRoutes.RelationshipsSegment}`);
    const relationShipPathMyCpg = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.RelationshipsSegment}`);
    const relationShipPath = app === DemographicAppKeys.myCPG ? relationShipPathMyCpg : relationShipPathMap;
    const dependentPath = `${relationShipPath}${AccountInfoRoutes.DependentSegment}`;

    const relationshipsProps = wizard?.getRelationShipSettings();

    const children = dependentsState?.dependents?.children;

    const classes = buttonStyles();
    const radiogroupClasses = radiogroupStyles();

    function OnSubmitHandler(
        values: DeathOfChildFormModel,
        formikHelpers: FormikProps<DeathOfChildFormModel>,
        pointOfContactFormValues?: PointOfContactFormModel) {

        setCurrentDomesticPartnerState({ values, formikHelpers, documentUploadState });

        wizard.onSubmit(
            values,
            formikHelpers,
            personGuid,
            documentUploadState,
            relationShipPath,
            setShowPointOfContact,
            pointOfContactFormValues,
        );
    }

    function OnPointOfContactSubmit(pointOfContactFormValues: PointOfContactFormModel) {
        if (currentDomesticPartnerState?.values) {

            wizard.onSubmit(
                currentDomesticPartnerState?.values,
                currentDomesticPartnerState?.formikHelpers,
                personGuid,
                currentDomesticPartnerState?.documentUploadState,
                relationShipPath,
                setShowPointOfContact,
                pointOfContactFormValues,
            );
        }
    }

    function OnPointOfContactCancel() {
        setShowPointOfContact(false);

        const loadedFiles = currentDomesticPartnerState?.documentUploadState?.stagedDocuments;
        // If there are files loaded, stage them again
        // The document state is reset when the contact form is displayed.
        if (loadedFiles && loadedFiles.length > 0) {
            dispatch(stageDocuments(loadedFiles, app));
        }
    }

    function RenderFormContent(formProps: FormikProps<DeathOfChildFormModel>) {

        return (
            <DividerSection removeDivider >
                <FormGroup>
                    <ContentSpacer>
                        <FormField
                            name='date_of_death'
                            label={relationshipsProps?.dateOfChildDeathLabel}
                            component='datePicker'
                            touched={formProps.touched}
                            errors={formProps.errors}
                            required
                            autoWidth
                        />
                    </ContentSpacer>
                </FormGroup>
            </DividerSection>);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function RenderDependents(formProps: FormikProps<DeathOfChildFormModel>) {
        if (children?.length > 0) {

            const groupName = 'selectedChildId';

            return (
                <>
                    <DividerSection removeDivider>
                        {
                            children.map(
                                (child) => {
                                    return (
                                        <div className={radiogroupClasses.radiogroup_details} data-formdata={groupName} key={child.person_guid}>
                                            <Field type="radio"
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                name={groupName} value={child?.person_guid} />
                                            <DependentDisplay
                                                dependent={child}
                                                dependentType={DependentTypes.child}
                                                basePath=''
                                                isReadonly
                                            />
                                        </div>);
                                })
                        }
                    </DividerSection>
                </>
            );
        }

        return (
            <DividerSection removeDivider>
                <ContentSpacer>
                    <Wysiwyg content={relationshipsProps?.noDependentsFoundMessage} />
                </ContentSpacer>
                <Link className={classes.secondaryButton} to={dependentPath}>{relationshipsProps?.addDependentText}</Link>
            </DividerSection>
        );
    }

    return (
        <PageContainer>
            <MainContent >
                { /* Hide the form but keep it rendering to keep the state */}
                <div className={showPointOfContact ? 'hide' : ''}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={OnSubmitHandler}
                        enableReinitialize
                        initialErrors={apiErrors}
                        validationSchema={wizard?.getValidationSchema()}
                    >
                        {(formProps: FormikProps<DeathOfChildFormModel>) => (
                            <>
                                <Form>
                                    <Grid>
                                        <FormWrapper
                                            formikProps={formProps}
                                            submitText={wizard?.getSubmitButtonText()}
                                            cancelText={wizard?.getCancelButtonText()}
                                            onCancel={() => wizard?.onCancel()}
                                            submitDisabled={wizard?.shouldDisableSubmit(formProps?.values)}
                                            successfulForms={sucessFormType}
                                            formType={formType}
                                            defaultShowDocUpload={wizard?.shouldShowDefaultDocumentUpload()}
                                            formKeys={Object.keys(initialValues)}
                                            additionalContentAboveButtons={<DeathOfChildAgreements formProps={formProps} wizard={wizard} />}
                                        >
                                            <PageHeader
                                                headerText={wizard?.getPageHeader()}
                                                description={wizard.getPageDescription()}
                                            />
                                            <Hidden mdUp>
                                                <SideBarContent>
                                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                                </SideBarContent>
                                            </Hidden>
                                            {RenderDependents(formProps)}
                                            {RenderFormContent(formProps)}
                                        </FormWrapper>
                                    </Grid>
                                </Form>
                            </>
                        )}
                    </Formik>
                </div>

                {showPointOfContact && <PointOfContact isMyCpg={app === DemographicAppKeys.myCPG} onSubmitHandler={OnPointOfContactSubmit} onCancelHandler={OnPointOfContactCancel} dependentType={dependentType} />}

            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>

        </PageContainer >

    );
}
